import { browserOffsetMinutes, clockPrecise, parseUtc } from "./timeline";
import type {
  SignUp,
  SpeakerChannel,
  SpeakerParticipationReport,
  Timeline,
  TimelineTurn
} from "./types";

/**
 * AB#9981. When each speaker started, when they stopped, and how long they actually spoke.
 *
 * <b>Why this module exists.</b> Los Angeles County asked for it in as many words: "it
 * would be very helpful if the individual speaker's start and stop talk times and the exact
 * amount of time spoken can be captured and displayed". The report already carried the
 * facts -- `timeProfile.timeline.turns` is `{ personKey, agendaItemId, startedUtc, endedUtc,
 * channel }` and has been since the timeline shipped -- but nothing outside the charts read
 * them. This module is the single place that turns those instants into the three values the
 * roster, the profile and both exports render, so the three surfaces cannot disagree about
 * what a speaker's turn was.
 *
 * <b>Pure.</b> No DOM, no `Date.now()`, no browser zone. The offset is an argument, resolved
 * once by {@link turnClock} from the server's `utcOffsetMinutesAtStart`, for the reason
 * `timeline.ts` states at length: half the zone ids the platform stores are Windows ids that
 * a browser's `Intl` cannot resolve, and a derived offset lands an hour out across a
 * daylight-saving boundary with nothing on screen to say so.
 *
 * <b>A duration is never invented.</b> An open turn -- the clerk started the timer and the
 * meeting moved on -- has a start and no end. It renders as "Not recorded" and is EXCLUDED
 * from every total, and the total says that it excluded it. Computing a length from the
 * current time would produce a figure that grew on every render, and putting a zero there
 * would say the speaker said nothing, which is a different fact.
 */

/** The words the report uses for a turn that was started and never closed. Donnie's sheet
 *  leaves the cell blank; a blank cell in a government record reads as lost data. */
export const NOT_RECORDED = "Not recorded";

/** Which clock the times are on, and whether the server chose it. */
export interface TurnClock {
  offsetMinutes: number;
  /** False means the server could not resolve the meeting's zone and these times are on
   *  the READER'S clock -- which every surface has to say out loud. */
  fromServer: boolean;
  timeZoneId: string | null;
}

/** One speaking turn, resolved against the agenda and the registration it belongs to. */
export interface SpeakingSession {
  personKey: string;
  agendaItemId: number;
  startMs: number;
  /** Null for an open turn. Never derived. */
  endMs: number | null;
  /** Whole seconds actually spoken. Null for an open turn. */
  seconds: number | null;
  channel: SpeakerChannel;
  /** From the registration for this person on this item, not a second heuristic. */
  languageCode: string | null;
  itemNumber: string;
  itemTitle: string;
}

/**
 * Turns indexed by the two grains the report reads them at: one PERSON (the profile) and
 * one person on one ITEM (a roster row). Built once per render.
 */
export interface TurnIndex {
  clock: TurnClock;
  byPerson: Record<string, SpeakingSession[]>;
  byPersonItem: Record<string, SpeakingSession[]>;
  /** True when at least one turn was indexed, so a caller can drop a column rather than
   *  render one that is "Not recorded" the whole way down. */
  any: boolean;
}

/**
 * The join key, LOWER-CASED.
 *
 * `PersonKey` and `SpeakerRecord.userId` are the same value, but they arrive through
 * different code paths and the platform's user ids are not case-normalised at rest. The
 * council fix on this branch already case-folds this join in the timeline assembler; doing
 * anything else here would reintroduce the bug one surface at a time.
 */
const key = (personKey: string): string => (personKey || "").toLowerCase();

const itemKey = (personKey: string, agendaItemId: number): string =>
  key(personKey) + "|" + agendaItemId;

/** The offset the whole report renders turn times on. */
export function turnClock(report: SpeakerParticipationReport): TurnClock {
  const timeline = report.timeProfile?.timeline as Timeline | undefined;
  const offset = timeline?.utcOffsetMinutesAtStart;
  const fromServer = typeof offset === "number" && Number.isFinite(offset);

  return {
    offsetMinutes: fromServer
      ? (offset as number)
      : browserOffsetMinutes(report.meeting.startUtc),
    fromServer,
    timeZoneId: timeline?.timeZoneId ?? null
  };
}

/** "(browser time zone)" or nothing, for a heading that has to say which clock it is on. */
export function clockNote(clock: TurnClock): string {
  return clock.fromServer ? "" : "(browser time zone)";
}

export function buildTurnIndex(report: SpeakerParticipationReport): TurnIndex {
  const clock = turnClock(report);
  const index: TurnIndex = { clock, byPerson: {}, byPersonItem: {}, any: false };

  const timeline = report.timeProfile?.timeline as Timeline | undefined;
  if (!timeline) return index;

  // Registration facts, resolved once. Language and channel are read off the SIGN-UP the
  // turn belongs to rather than guessed from the turn: there is one channel heuristic in
  // this codebase and it lives on the registration.
  const signUps: Record<string, SignUp> = {};
  const items: Record<number, { number: string; title: string }> = {};

  for (const section of report.sections ?? []) {
    for (const item of section.items) {
      items[item.id] = { number: item.itemNumber, title: item.title };
      for (const signUp of item.signUps) {
        signUps[itemKey(signUp.userId, signUp.agendaItemId)] = signUp;
      }
    }
  }

  for (const turn of (timeline.turns ?? []) as TimelineTurn[]) {
    const startMs = parseUtc(turn.startedUtc);
    if (startMs === null) continue;

    const rawEnd = parseUtc(turn.endedUtc);

    // An end that does not FOLLOW the start is not a measurement -- a clock skew, or a
    // double-click on the timer. The server already refuses to derive a duration from one;
    // refusing it here too means the client cannot disagree with the server about whether
    // a turn was closed.
    const endMs = rawEnd !== null && rawEnd > startMs ? rawEnd : null;

    const lookup = itemKey(turn.personKey, turn.agendaItemId);
    const signUp = signUps[lookup];
    const item = items[turn.agendaItemId];

    const session: SpeakingSession = {
      personKey: turn.personKey,
      agendaItemId: turn.agendaItemId,
      startMs,
      endMs,
      seconds: endMs === null ? null : Math.round((endMs - startMs) / 1000),
      channel: signUp ? signUp.channel : turn.channel,
      languageCode: signUp ? signUp.languageCode : null,
      itemNumber: item ? item.number : "",
      itemTitle: item ? item.title : ""
    };

    const personKeyLower = key(turn.personKey);
    if (!index.byPerson[personKeyLower]) index.byPerson[personKeyLower] = [];
    index.byPerson[personKeyLower].push(session);

    if (!index.byPersonItem[lookup]) index.byPersonItem[lookup] = [];
    index.byPersonItem[lookup].push(session);
    index.any = true;
  }

  // Chronological, because these read as a record of the meeting rather than as a table to
  // be sorted. A stable secondary on the agenda item keeps two turns stamped in the same
  // second in a deterministic order across reads.
  const chronological = (a: SpeakingSession, b: SpeakingSession): number =>
    a.startMs - b.startMs || a.agendaItemId - b.agendaItemId;

  for (const bucket of Object.keys(index.byPerson)) index.byPerson[bucket].sort(chronological);
  for (const bucket of Object.keys(index.byPersonItem)) {
    index.byPersonItem[bucket].sort(chronological);
  }

  return index;
}

/**
 * The index for a report, built at most once.
 *
 * Keyed on the payload OBJECT, not on the meeting id, so a re-fetch produces a fresh index
 * rather than serving a stale one -- and held weakly, so an index cannot keep a discarded
 * payload alive. The renderer reaches the roster through five nested functions and the
 * directory through four; threading the index down both chains would be six signature
 * changes whose only invariant -- "everyone gets the same index" -- this enforces directly.
 *
 * `GetOrAdd`-shaped rather than check-then-act: one `has` guarded `set`, no window where
 * two callers each build one.
 */
const indexes = new WeakMap<SpeakerParticipationReport, TurnIndex>();

export function turnIndexFor(report: SpeakerParticipationReport): TurnIndex {
  const existing = indexes.get(report);
  if (existing) return existing;

  const built = buildTurnIndex(report);
  indexes.set(report, built);
  return built;
}

export function sessionsForPerson(index: TurnIndex, userId: string): SpeakingSession[] {
  return index.byPerson[key(userId)] || [];
}

export function sessionsForSignUp(index: TurnIndex, signUp: SignUp): SpeakingSession[] {
  return index.byPersonItem[itemKey(signUp.userId, signUp.agendaItemId)] || [];
}

/** The instant a person first had the microphone on this item, or null. */
export function firstStartMs(sessions: SpeakingSession[]): number | null {
  return sessions.length === 0 ? null : sessions[0].startMs;
}

/**
 * The instant they last let go of it, or null.
 *
 * The last CLOSED end, not the last turn's end: a person whose final turn was left open has
 * no recorded stop, and reporting the previous turn's end as their stop time would state a
 * fact the record does not hold.
 */
export function lastEndMs(sessions: SpeakingSession[]): number | null {
  let last: number | null = null;
  for (const session of sessions) {
    if (session.endMs !== null && (last === null || session.endMs > last)) last = session.endMs;
  }
  return last;
}

/**
 * Seconds actually spoken across a set of turns, and whether any of them were left out.
 *
 * `seconds` is null when there is nothing to add up at all, which is a different fact from
 * a sum of zero closed turns -- and `excluded` is what lets the caller say so on the total
 * instead of quietly under-reporting.
 */
export function spokenSeconds(
  sessions: SpeakingSession[]
): { seconds: number | null; excluded: number } {
  let total = 0;
  let counted = 0;
  let excluded = 0;

  for (const session of sessions) {
    if (session.seconds === null) excluded += 1;
    else {
      total += session.seconds;
      counted += 1;
    }
  }

  return { seconds: counted === 0 ? null : total, excluded };
}

/**
 * Decimal minutes to two places -- "1.00", "0.90".
 *
 * The requesting clerk's own column is in decimal minutes, not m:ss, because their sheet
 * sums it. `duration()` in format.ts stays m:ss for the Length column, which is the one a
 * reader scans rather than adds; the two are deliberately different shapes for deliberately
 * different jobs, and both are labelled.
 */
export function decimalMinutes(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return NOT_RECORDED;
  return (Math.max(0, seconds) / 60).toFixed(2);
}

/** "10:00:00 AM", on the meeting's clock, or "Not recorded". */
export function turnStamp(ms: number | null, clock: TurnClock): string {
  return ms === null ? NOT_RECORDED : clockPrecise(ms, clock.offsetMinutes);
}

/** The ISO instant behind a rendered stamp, for a `data-*` attribute a test can read
 *  without having to reimplement the clock. */
export function isoOrEmpty(ms: number | null): string {
  return ms === null ? "" : new Date(ms).toISOString();
}
